import { v4 } from "uuid";

import {
  DiscountApplicationMethodType,
  DiscountApplicationType,
  DiscountDiscountType,
  DiscountSettingFieldType,
  GenericVariationFe,
  OfferDto,
  OfferTierUnitFieldType,
  ShippingDiscountDto,
  VariationDto,
} from "@intelligems/ig-types";
import { createSlice } from "@reduxjs/toolkit";
import { type PayloadAction } from "@reduxjs/toolkit";
import { fromGqlId } from "@src/api/formatters/shopify";
import {
  initialOfferState,
  offerTestOfferVariationInitial,
} from "@src/api/initials";
import {
  addTier,
  removeTier,
  setUnit,
  toggleDiscountUnitType,
} from "@src/api/reducers/discounts/sharedDiscountReducerActions";
import { RootState } from "@src/store";

import { IOffersState } from "./types";

const initialState: IOffersState = {
  isSuccess: false,
  offers: [],
  storedOffers: [],
};

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    resetOffer: () => initialState,
    resetOfferToStored: (draft) => {
      draft.offers = [];
    },
    addOfferTier: (
      draft,
      action: PayloadAction<{
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;

      const index = getOfferIndexByVariationId(
        draft.offers,
        action.payload.variationId
      );
      draft.offers[index] = addTier(draft.offers[index]);

      return draft;
    },
    updateOfferName: (
      draft,
      action: PayloadAction<{
        name: string;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;

      const index = getOfferIndexByVariationId(
        draft.offers,
        action.payload.variationId
      );
      draft.offers[index].name = action.payload.name;

      return draft;
    },
    initPersonalizationOffer: (
      draft,
      action: PayloadAction<{
        variationId: string;
        discountApplicationType?: DiscountApplicationType;
        discountApplicationMethod?: DiscountApplicationMethodType;
      }>
    ) => {
      draft.isSuccess = true;

      const newOffer = initialOfferState({
        variationId: action.payload.variationId,
        discountApplicationType: action.payload.discountApplicationType,
        discountApplicationMethod: action.payload.discountApplicationMethod,
      });

      draft.offers.push(newOffer);

      return draft;
    },

    initOffersFromVariations: (
      draft,
      action: PayloadAction<{
        variations: VariationDto[];
        isInitialize?: boolean;
      }>
    ) => {
      const { variations, isInitialize } = action.payload;

      const tempOffers: OfferDto[] = isInitialize ? [] : draft.offers;
      const tempOffersVariationIds: string[] = isInitialize
        ? []
        : draft.offers.map((offer) => offer.variationId);

      variations.forEach((variation) => {
        if (variation.offer) {
          if (tempOffersVariationIds.includes(variation.id)) {
            const index = tempOffers.findIndex(
              (offer) => offer.variationId === variation.id
            );
            if (index > -1) {
              tempOffers[index] = { ...variation.offer };
            }
          } else {
            tempOffers.push(variation.offer);
          }
        }
      });

      if (isInitialize) {
        draft.isSuccess = true;
        draft.offers = tempOffers;
        draft.storedOffers = tempOffers;
      }
      return draft;
    },
    setVariationOffer: (
      draft,
      action: PayloadAction<{
        variation: GenericVariationFe;
        offer: OfferDto;
      }>
    ) => {
      const { variation, offer } = action.payload;

      const foundVariationOfferIndex = draft.offers.findIndex(
        (offer) => offer.variationId === variation.id
      );

      if (foundVariationOfferIndex !== -1) {
        draft.offers[foundVariationOfferIndex] = offer;
      } else {
        draft.offers.push(offer);
      }

      return draft;
    },
    removeOfferTier: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { variationId, tierIndex } = action.payload;

      const index = getOfferIndexByVariationId(draft.offers, variationId);
      draft.offers[index] = removeTier(draft.offers[index], tierIndex);

      return draft;
    },
    setOfferUnit: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        key: OfferTierUnitFieldType;
        value: number;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, key, value, variationId } = action.payload;

      const index = getOfferIndexByVariationId(draft.offers, variationId);

      draft.offers[index] = setUnit(draft.offers[index], key, tierIndex, value);

      return draft;
    },
    setDiscountTitle: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: string;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, value, variationId } = action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      tier.discountTitle = value || null;

      return draft;
    },
    setGWPTitle: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: string;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;

      const { tierIndex, variationId, value } = action.payload;
      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);

      tier.giftWithPurchaseTitle = value;

      return draft;
    },
    toggleOfferType: (
      draft,
      action: PayloadAction<{
        key: DiscountSettingFieldType;
        value: string;
        variationId: string;
      }>
    ) => {
      const { offers } = draft;
      if (!offers) return draft;
      const { key, value, variationId } = action.payload;

      if (key === "unitType") {
        const index = getOfferIndexByVariationId(offers, variationId);
        offers[index] = toggleDiscountUnitType(offers[index], action.payload);
      } else {
        const offer = getOfferByVariationId(offers, variationId);
        offer.discountType = value as DiscountDiscountType;
        if (value === "percentage") {
          offer.discountType = value as DiscountDiscountType;
        } else {
          offer.discountType = "dollar";
          if (value === "dollarPerOrder") {
            offer.discountApplicationMethod = "perOrder";
          } else {
            offer.discountApplicationMethod = "perItem";
          }
        }
      }
      return draft;
    },
    toggleFreeShipping: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, variationId } = action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      tier.isFreeShipping = !tier.isFreeShipping;

      if (tier.isFreeShipping) {
        const offer = getOfferByVariationId(draft.offers, variationId);
        if (!offer.shippingDiscount) {
          offer.shippingDiscount = {
            id: v4(),
            discountType: "percentage",
            unitDiscount: 100,
            countryFilter: null,
            rateFilter: null,
            discountLabel: null,
          };
        }
      }

      return draft;
    },
    toggleFreeShippingCascading: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, variationId } = action.payload;
      const offer = getOfferByVariationId(draft.offers, variationId);

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      const newState = !tier.isFreeShipping;
      if (newState) {
        for (let i = tierIndex; i < offer.tiers.length; i++) {
          offer.tiers[i].isFreeShipping = true;
        }
        if (!offer.shippingDiscount) {
          offer.shippingDiscount = {
            id: v4(),
            discountType: "percentage",
            unitDiscount: 100,
            countryFilter: null,
            rateFilter: null,
            discountLabel: null,
          };
        }
      } else {
        tier.isFreeShipping = false;
      }

      return draft;
    },
    toggleAutoAddGWP: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: boolean;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, value, variationId } = action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);

      tier.autoAddGiftWithPurchase =
        value && tier.giftWithPurchaseVariantId !== null;

      return draft;
    },
    toggleGiftWithPurchase: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        value: boolean;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, value, variationId } = action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      tier.isGiftWithPurchase = value;
      if (!value) {
        tier.giftWithPurchaseTitle = null;
      }

      return draft;
    },
    toggleCombinesWithIntelligems: (
      draft,
      action: PayloadAction<{
        value: boolean;
        variationId: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.offers) return draft;
      const { value, variationId } = action.payload;

      const offer = getOfferByVariationId(draft.offers, variationId);

      offer.combinesWithIntelligems = value;

      return draft;
    },
    toggleCombinesWithShopify: (
      draft,
      action: PayloadAction<{
        value: boolean;
        variationId: string;
      }>
    ) => {
      if (!draft.isSuccess || !draft.offers) return draft;
      const { value, variationId } = action.payload;

      const offer = getOfferByVariationId(draft.offers, variationId);

      offer.combinesWithShopify = value;

      return draft;
    },
    setOfferGwp: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        productId: string | null;
        variantId: string | null;
        handle: string | null;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, productId, variantId, handle, variationId } =
        action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      tier.giftWithPurchaseVariantId = variantId ? fromGqlId(variantId) : null;
      tier.giftWithPurchaseProductId = productId ? fromGqlId(productId) : null;
      tier.autoAddGiftWithPurchase = variantId !== null;
      tier.giftWithPurchaseHandle = handle;

      return draft;
    },
    removeOfferGwp: (
      draft,
      action: PayloadAction<{
        tierIndex: number;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { tierIndex, variationId } = action.payload;

      const tier = getTierByVariationId(draft.offers, variationId, tierIndex);
      tier.giftWithPurchaseVariantId = undefined;
      tier.giftWithPurchaseProductId = undefined;
      tier.giftWithPurchaseHandle = undefined;

      return draft;
    },
    setMaximumOfferAmount: (
      draft,
      action: PayloadAction<{
        maximumDiscountAmount: number | null;
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { variationId, maximumDiscountAmount } = action.payload;

      const offer = getOfferByVariationId(draft.offers, variationId);
      offer.maximumDollarDiscount = maximumDiscountAmount;

      return draft;
    },
    resetOfferTestOffer: (
      draft,
      action: PayloadAction<{
        variationId: string;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { variationId } = action.payload;

      // Filter out the offer with the matching variationId
      draft.offers = draft.offers.filter(
        (offer) => offer.variationId !== variationId
      );

      return draft;
    },
    setShippingDiscount: (
      draft,
      action: PayloadAction<{
        variationId: string;
        discount?: ShippingDiscountDto;
      }>
    ) => {
      if (!draft.offers) return draft;
      const { variationId, discount } = action.payload;

      const offer = getOfferByVariationId(draft.offers, variationId);
      offer.shippingDiscount = discount
        ? {
            ...discount,
            countryFilter: discount?.countryFilter ?? null,
            rateFilter: discount?.rateFilter ?? null,
          }
        : null;
    },
  },
  selectors: {
    selectedOffers: (state) => state.offers,
    selectedOfferState: (state) => state,
    selectedStoredOffer: (state) => state.storedOffers,
  },
});

export const {
  resetOffer,
  toggleGiftWithPurchase,
  toggleFreeShipping,
  toggleFreeShippingCascading,
  toggleOfferType,
  toggleCombinesWithIntelligems,
  toggleCombinesWithShopify,
  removeOfferTier,
  setOfferUnit,
  setDiscountTitle,
  setGWPTitle,
  removeOfferGwp,
  setOfferGwp,
  setMaximumOfferAmount,
  toggleAutoAddGWP,
  addOfferTier,
  initOffersFromVariations,
  initPersonalizationOffer,
  resetOfferToStored,
  setVariationOffer,
  resetOfferTestOffer,
  setShippingDiscount,
  updateOfferName,
} = offersSlice.actions;

export const offersReducer = offersSlice.reducer;
export const { selectedOffers, selectedStoredOffer, selectedOfferState } =
  offersSlice.selectors;

function getOfferIndexByVariationId(offers: OfferDto[], variationId: string) {
  let index = 0;

  const foundIndex = offers.findIndex(
    (findOffer) => findOffer.variationId === variationId
  );
  if (foundIndex > -1) {
    index = foundIndex;
  }
  return index;
}

function getOfferByVariationId(offers: OfferDto[], variationId: string) {
  const index = getOfferIndexByVariationId(offers, variationId);
  return offers[index];
}

export function getTierByVariationId(
  offers: OfferDto[],
  variationId: string,
  tierIndex: number
) {
  const offer = getOfferByVariationId(offers, variationId);
  return offer.tiers[tierIndex];
}
